import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogOut, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * <LogoutDialog />
 * --------------------------------------------------------------
 * Premium "Sign out?" confirmation dialog. Supports BOTH:
 *   1) Controlled mode (preferred for global header logout):
 *        <LogoutDialog
 *          controlledOpen={logoutOpen}
 *          onControlledOpenChange={setLogoutOpen}
 *          afterLogout={...}
 *        />
 *      — no children required; trigger is owned by the parent.
 *      — render this at the top of a layout so it survives dropdowns/menus
 *        being unmounted while the dialog is in flight.
 *
 *   2) Uncontrolled mode (legacy / inline triggers):
 *        <LogoutDialog>{({ open }) => <button onClick={open}>Sign out</button>}</LogoutDialog>
 *
 * Either way, the actual logout call (and the toast) is handled inside
 * `auth.logout()`.
 *
 * Props:
 *  - controlledOpen / onControlledOpenChange: controlled-mode signals.
 *  - children: function ({ open }) => ReactNode  OR  trigger node.
 *      If function: receives an `open()` callback to invoke from anywhere.
 *      If not provided and uncontrolled: renders a default destructive button.
 *  - afterLogout: optional callback once logout completes.
 */
export default function LogoutDialog({
  children,
  afterLogout,
  controlledOpen,
  onControlledOpenChange,
}) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isControlled = typeof controlledOpen === "boolean";
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const setOpen = (next) => {
    if (isControlled) {
      if (typeof onControlledOpenChange === "function") {
        onControlledOpenChange(next);
      }
    } else {
      setUncontrolledOpen(next);
    }
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await logout();
      if (typeof afterLogout === "function") afterLogout();
      else navigate("/");
    } finally {
      setSubmitting(false);
      setOpen(false);
    }
  };

  let trigger = null;
  if (!isControlled) {
    trigger = typeof children === "function"
      ? children({ open: () => setOpen(true) })
      : children || (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 text-sm text-rose-300 hover:text-rose-200 transition-colors"
            data-testid="logout-trigger"
          >
            <LogOut className="w-4 h-4" />
            {t("nav.logout", "Sign out")}
          </button>
        );
  }

  return (
    <>
      {trigger}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent
          className="bg-[#0F1116] border-white/10 text-white"
          data-testid="logout-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl gold-text" data-testid="logout-dialog-title">
              {t("auth.logout_confirm_title", "Sign out?")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/65 text-sm leading-relaxed">
              {t("auth.logout_confirm_message", "You\u2019ll need to sign in again to access your account and admin tools.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel
              className="bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white"
              data-testid="logout-cancel"
            >
              {t("auth.logout_confirm_cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleConfirm(); }}
              disabled={submitting}
              className="bg-gradient-to-r from-[#C8A24A] to-[#7A5F2A] text-black hover:from-[#E2C77A] hover:to-[#7A5F2A] inline-flex items-center gap-2"
              data-testid="logout-confirm"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
              {t("auth.logout_confirm_yes", "Sign out")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
